import { getStorefrontClient } from './storefrontClient'

const DEFAULT_STORE_DOMAIN = 'helinhair-2.myshopify.com'

// ---- GraphQL fragments ------------------------------------------------------

// Inline fragment used by all cart mutations so every response carries line IDs
// alongside the checkout URL — needed for subsequent update/remove operations.
const CART_LINES_FRAGMENT = `
  id
  checkoutUrl
  lines(first: 250) {
    nodes {
      id
      quantity
      merchandise { ... on ProductVariant { id } }
    }
  }
`

// ---- GraphQL operations -----------------------------------------------------

const CART_CREATE_MUTATION = `
  mutation CartCreate($lines: [CartLineInput!]!) {
    cartCreate(input: { lines: $lines }) {
      cart { ${CART_LINES_FRAGMENT} }
      userErrors { message field }
      warnings { code message }
    }
  }
`

const CART_LINES_ADD_MUTATION = `
  mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart { ${CART_LINES_FRAGMENT} }
      userErrors { message field }
      warnings { code message }
    }
  }
`

const CART_LINES_UPDATE_MUTATION = `
  mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { ${CART_LINES_FRAGMENT} }
      userErrors { message field }
      warnings { code message }
    }
  }
`

const CART_LINES_REMOVE_MUTATION = `
  mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart { ${CART_LINES_FRAGMENT} }
      userErrors { message field }
      warnings { code message }
    }
  }
`

const CART_CHECKOUT_URL_QUERY = `
  query CartCheckoutUrl($cartId: ID!) {
    cart(id: $cartId) {
      checkoutUrl
    }
  }
`

// ---- Internal types ---------------------------------------------------------

interface SfCartLine {
  id: string
  merchandise: { id: string }
}

interface SfCartPayload {
  cart: {
    id: string
    checkoutUrl: string
    lines: { nodes: SfCartLine[] }
  } | null
  userErrors: { message: string; field: string[] }[]
  warnings: { code: string; message: string }[]
}

// ---- Helpers ----------------------------------------------------------------

/**
 * Forces the checkoutUrl returned by the Cart API onto the *.myshopify.com
 * domain so it bypasses the Online Store password protection.
 *
 * VITE_SHOPIFY_DOMAIN must be the *.myshopify.com host (e.g.
 * "helinhair-2.myshopify.com"). If it is a custom domain the rewrite is
 * skipped and a warning is logged in dev.
 */
export function toMyshopifyCheckoutUrl(url: string): string {
  const domain = (import.meta.env.VITE_SHOPIFY_DOMAIN as string | undefined)
    ?.trim()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '') ?? ''

  if (!domain.includes('.myshopify.com')) {
    if (import.meta.env.DEV) {
      console.warn(
        '[shop] checkoutUrl not normalised: VITE_SHOPIFY_DOMAIN must be your ' +
          '*.myshopify.com host so checkout bypasses the storefront password.',
      )
    }
    return url
  }

  try {
    const parsed = new URL(url)
    parsed.hostname = domain
    return parsed.toString()
  } catch {
    return url
  }
}


function toMerchandiseLines(items: CheckoutLine[]) {
  return items
    .filter((i) => i.variantId && i.quantity > 0)
    .map((i) => ({
      merchandiseId: `gid://shopify/ProductVariant/${i.variantId}`,
      quantity: i.quantity,
    }))
}

/** Extracts the numeric ID segment from a Shopify GID. */
function gidTail(gid: string): string {
  // String.split always yields a non-empty array, so pop() is never undefined;
  // the `?? ''` satisfies the type checker but is unreachable.
  /* v8 ignore next */
  return gid.split('/').pop() ?? ''
}

/** Maps Shopify cart line nodes to CartLine[]. variantId is the numeric string. */
function mapLines(nodes: SfCartLine[]): CartLine[] {
  return nodes.map((n) => ({
    lineId: n.id,
    variantId: gidTail(n.merchandise.id),
  }))
}

/** Parses a cart mutation payload, logs warnings/errors, and returns the result. */
function parseCartPayload(payload: SfCartPayload, source: string): ShopifyCartResult | null {
  if (!payload.cart) {
    console.error(`[cart] ${source} returned no cart`)
    return null
  }
  if (payload.userErrors.length > 0) {
    console.error(`[cart] ${source} userErrors:`, payload.userErrors)
    return null
  }
  if (payload.warnings.length > 0) {
    console.warn(`[cart] ${source} warnings:`, payload.warnings)
  }
  const raw = payload.cart.checkoutUrl
  const checkoutUrl = toMyshopifyCheckoutUrl(raw)
  console.info(`[cart] ${source} raw:`, raw, '→ final:', checkoutUrl)
  return {
    cartId: payload.cart.id,
    checkoutUrl,
    lines: mapLines(payload.cart.lines.nodes),
  }
}

// ---- Public types -----------------------------------------------------------

export interface CheckoutLine {
  variantId: string
  quantity: number
}

/** A single line in the Shopify cart, keyed by the Shopify line GID. */
export interface CartLine {
  /** Shopify cart line GID e.g. gid://shopify/CartLine/abc123 */
  lineId: string
  /** Numeric variant ID string — matches CartItem.variantId in the store. */
  variantId: string
}

export interface ShopifyCartResult {
  cartId: string
  checkoutUrl: string
  /** Current lines with their Shopify line IDs — use for subsequent update/remove calls. */
  lines: CartLine[]
}

// ---- Cart API ---------------------------------------------------------------

/**
 * Creates a new Shopify cart with the given lines.
 * Returns the Cart ID, checkout URL, and line ID mapping, or null on failure.
 */
export async function shopifyCartCreate(items: CheckoutLine[]): Promise<ShopifyCartResult | null> {
  const lines = toMerchandiseLines(items)
  if (!lines.length) return null
  try {
    const client = getStorefrontClient()
    const { data, errors } = await client.request<{
      cartCreate: SfCartPayload
    }>(CART_CREATE_MUTATION, { variables: { lines } })
    if (errors) {
      console.error('[cart] cartCreate graphql errors:', errors)
      return null
    }
    if (!data?.cartCreate) return null
    const result = parseCartPayload(data.cartCreate, 'cartCreate')
    if (result) console.info('[cart] cartCreate → cartId:', result.cartId, 'lines:', result.lines.length)
    return result
  } catch (e) {
    console.error('[cart] cartCreate threw:', e)
    return null
  }
}

/**
 * Adds lines to an existing Shopify cart.
 * Returns the updated cart result (all lines, including newly added), or null on failure.
 */
export async function shopifyCartLinesAdd(
  cartId: string,
  items: CheckoutLine[],
): Promise<ShopifyCartResult | null> {
  const lines = toMerchandiseLines(items)
  if (!lines.length) return null
  try {
    const client = getStorefrontClient()
    const { data, errors } = await client.request<{
      cartLinesAdd: SfCartPayload
    }>(CART_LINES_ADD_MUTATION, { variables: { cartId, lines } })
    if (errors || !data?.cartLinesAdd) return null
    return parseCartPayload(data.cartLinesAdd, 'cartLinesAdd')
  } catch {
    return null
  }
}

/**
 * Updates line quantities in an existing Shopify cart.
 * Returns the updated cart result or null on failure (e.g. cart expired).
 */
export async function shopifyCartLinesUpdate(
  cartId: string,
  lines: { lineId: string; quantity: number }[],
): Promise<ShopifyCartResult | null> {
  if (!lines.length) return null
  try {
    const client = getStorefrontClient()
    const { data, errors } = await client.request<{
      cartLinesUpdate: SfCartPayload
    }>(CART_LINES_UPDATE_MUTATION, {
      variables: {
        cartId,
        lines: lines.map(({ lineId, quantity }) => ({ id: lineId, quantity })),
      },
    })
    if (errors || !data?.cartLinesUpdate) return null
    return parseCartPayload(data.cartLinesUpdate, 'cartLinesUpdate')
  } catch {
    return null
  }
}

/**
 * Removes lines from an existing Shopify cart by their line GIDs.
 * Returns the updated cart result or null on failure (e.g. cart expired).
 */
export async function shopifyCartLinesRemove(
  cartId: string,
  lineIds: string[],
): Promise<ShopifyCartResult | null> {
  if (!lineIds.length) return null
  try {
    const client = getStorefrontClient()
    const { data, errors } = await client.request<{
      cartLinesRemove: SfCartPayload
    }>(CART_LINES_REMOVE_MUTATION, { variables: { cartId, lineIds } })
    if (errors || !data?.cartLinesRemove) return null
    return parseCartPayload(data.cartLinesRemove, 'cartLinesRemove')
  } catch {
    return null
  }
}

/**
 * Queries a fresh checkout URL for an existing cart.
 * Returns null when the cart ID is invalid or has expired.
 */
export async function shopifyCartGetCheckoutUrl(cartId: string): Promise<string | null> {
  try {
    const client = getStorefrontClient()
    const { data, errors } = await client.request<{
      cart: { checkoutUrl: string } | null
    }>(CART_CHECKOUT_URL_QUERY, { variables: { cartId } })
    if (errors || !data?.cart?.checkoutUrl) return null
    return toMyshopifyCheckoutUrl(data.cart.checkoutUrl)
  } catch {
    return null
  }
}

// ---- Storefront domain / permalink (legacy fallback) ------------------------

/**
 * Returns the myshopify.com domain for building cart URLs.
 */
export function shopifyStoreDomain(): string {
  const raw = (import.meta.env.VITE_SHOPIFY_DOMAIN as string | undefined)?.trim()
  if (raw && raw.length > 0) {
    return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '')
  }
  console.warn(
    '[shop] VITE_SHOPIFY_DOMAIN not set — using prod store as fallback.',
  )
  return DEFAULT_STORE_DOMAIN
}

/**
 * Builds a Shopify /cart/{variantId}:{qty},... permalink as a last resort
 * when the Cart API is unavailable. Returns null when lines or domain are missing.
 */
export function shopifyCartUrl(items: CheckoutLine[], domain: string | undefined): string | null {
  const host = domain?.trim().replace(/^https?:\/\//, '').replace(/\/+$/, '')
  if (!host) return null
  const line = items
    .filter((i) => i.variantId && i.quantity > 0)
    .map((i) => `${i.variantId}:${i.quantity}`)
    .join(',')
  if (!line) return null
  return `https://${host}/cart/${line}`
}
